import React, {useState} from "react";
import {useMutation} from '@tanstack/react-query';
import {useMount} from "react-use";
import ProjectPosition, {
    linkGreenlightJobToCandidate, refreshGreenlightStatus,
    requestGreenlightTaxClassVerificationForPosition,
    unlinkGreenlightJobToCandidate,
    updateContractTerms,
    updateTalentContract
} from "../models/ProjectPosition";
import LoadingBanner from "../components/LoadingBanner";
import {useProject} from "./ClientProjectProvider";
import {usePosition} from "./ProjectPositionProvider";
import TalentCandidate from "../models/TalentCandidate";

let PositionOverrideContext = React.createContext(null);

const PositionOverrideProvider = ({children, positionId}) => {


    const {
        project,
        isLoading: isProjectLoading
    } = useProject();
    const {
        position
    } = usePosition();

    const isFixedFee = project.isFixedFee();
    const isFixedRetainer = project.isFixedRetainer();

    const [isLoading, setIsLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);

    const [assignedModel, setAssignedModel] = useState(null);

    const setNormalizedAssignedModel = async(candidate) => {
        if (candidate.confirmed_started_at) {
            candidate.confirmed_started_at = TB.moment(candidate.confirmed_started_at)
        }
        if (candidate.confirmed_ended_at) {
            candidate.confirmed_ended_at = TB.moment(candidate.confirmed_ended_at)
        }
        candidate.display_percent_markup = Number((parseFloat((candidate.percent_markup)) * 100).toFixed(4));

        //TODO Get values from backend
        if (isFixedFee) {
            if (!candidate.exact_premarkup_rate) {
                candidate.exact_premarkup_rate = candidate?.hourly_rate;
            }
            if (!candidate.premarkup_rate) {
                candidate.premarkup_rate = candidate?.hourly_rate;
            }
            if (!candidate.exact_platform_rate) {
                candidate.exact_platform_rate = position.exact_platform_rate;
            }
            if (!candidate.contract_value) {
                candidate.contract_value = position?.contract_value;
            }
        }

        if (isFixedRetainer) {
            candidate.total_periods = position.actual_total_periods;
            candidate.payout_amount = position.isFixedRetainerCostPerPeriodBased() ? candidate.talent_opps?.[0]?.payout_amount :
                (candidate.estimated_payout_amount / candidate.total_periods)
            candidate.platform_cost = candidate.talent_opps?.[0]?.platform_cost || (candidate.exact_platform_cost / candidate.total_periods);
            candidate.total_payout_amount = candidate.estimated_payout_amount;
            candidate.contract_value = candidate.exact_platform_cost;
        }
        console.log('setNormalizedAssignedModel', candidate.id,candidate.platform_cost)
        await setAssignedModel(TalentCandidate.make(candidate))
    }

    useMount(async() => {
        if (TB.isDefined(position?.assigned_candidate?.job_candidate)) {
            await setNormalizedAssignedModel({...position.assigned_candidate?.job_candidate})
        }

        await setIsLoading(false)
    })

    const handlePositionUpdated = async(updatedPosition) => {
        console.log('PositionOverrideProvider @ handlePositionUpdated', updatedPosition);
        if (!(updatedPosition instanceof ProjectPosition)) {
            updatedPosition = ProjectPosition.make({...updatedPosition})
        }
        await updatedPosition.refreshInProjectCacheFor(project)
        await setNormalizedAssignedModel({...updatedPosition.assigned_candidate?.job_candidate})
    }

    const onRequestError = async(e) => {
        await setIsDisabled(false)
        return  TB.showServerErrorToast()
    }

    const onRequestSuccess = async(updatedPosition) => {
        await handlePositionUpdated(updatedPosition)
        await setIsDisabled(false)
    }

    const terminateCandidateForGreenLightMutation = useMutation(
        ['terminateCandidateForGreenLight'], async(params) => {
            await setIsDisabled(true);

            return position.terminateCandidateForGreenLight(project?.id)
        }, {
            onError: onRequestError,
            onSuccess: onRequestSuccess
        }
    )

    const updateTaxPreferenceMutation = useMutation(
        ['updateTaxPreference'], async(params) => {
            await setIsDisabled(true);

            return requestGreenlightTaxClassVerificationForPosition(project?.id,position?.id, params)
        }, {
            onError: onRequestError,
            onSuccess: onRequestSuccess
        }
    )
    const refreshGreenlightStatusMutation = useMutation(
        ['refreshGreenlightStatus'], async(params) => {
            await setIsDisabled(true);

            return refreshGreenlightStatus(project?.id,position?.id, params)
        }, {
            onError: onRequestError,
            onSuccess: onRequestSuccess
        }
    )

    const updateContractTermsMutation = useMutation(
        ['updateContractTerms'], async(params) => {
            await setIsDisabled(true);

            return updateContractTerms(project?.id,position?.id, params)
        }, {
            onError: onRequestError,
            onSuccess: onRequestSuccess
        }
    )
    const linkGreenlightJobToCandidateMutation = useMutation(
        ['linkGreenlightJobToCandidate'], async(greenlight_job_id) => {
            await setIsDisabled(true);

            return linkGreenlightJobToCandidate(project?.id,position?.id, greenlight_job_id)
        }, {
            onError: onRequestError,
            onSuccess: onRequestSuccess
        }
    )
    const unlinkGreenlightJobToCandidateMutation = useMutation(
        ['unlinkGreenlightJobToCandidate'], async(params) => {
            await setIsDisabled(true);

            return unlinkGreenlightJobToCandidate(project?.id,position?.id)
        }, {
            onError: onRequestError,
            onSuccess: onRequestSuccess
        }
    )

    const updateTalentContractMutation = useMutation(
        ['updateTalentContract'],
        async(params) => {

            return updateTalentContract(project?.id, position.id, params)
        }, {
            onError: onRequestError,
            onSuccess: onRequestSuccess
        }
    )

    const value = {
        assignedModel,
        isLoading,
        isDisabled: isDisabled ||
            updateContractTermsMutation.isLoading ||
            updateTalentContractMutation.isLoading ||
            updateTaxPreferenceMutation.isLoading ||
            terminateCandidateForGreenLightMutation.isLoading ||
            updateTalentContractMutation.isLoading ||
            linkGreenlightJobToCandidateMutation.isLoading ||
            refreshGreenlightStatusMutation.isLoading ||
            unlinkGreenlightJobToCandidateMutation.isLoading,
        handlePositionUpdated,
        onChange: (params) => {
            setNormalizedAssignedModel({...assignedModel, ...params})
        },
        updateTalentContract: (params) => {
            return updateTalentContractMutation.mutate(params)
        },
        onReset: () => {
            setNormalizedAssignedModel({...position.assigned_candidate?.job_candidate})
        },
        updateContractTerms: (params) => {
            return updateContractTermsMutation.mutateAsync(params)
        },
        linkGreenlightJobToCandidate: (params) => {
            return linkGreenlightJobToCandidateMutation.mutateAsync(params)
        },
        unlinkGreenlightJobToCandidate: async () => {
            if (!position.assigned_candidate?.greenlight_id) {
                return;
            }
            let confirm = await TB.alert('warning', 'Unlink Job',
                'Note: this option will not terminate the Greenlight Job. ' +
                'If you continue, you can link this candidate to a different Greenlight Job or change the tax preference. ' +
                'Are you sure you want to continue? ', [
                    {title: 'Yes, Continue'},
                    {title: 'Cancel'}
                ]
            )

            if (confirm) {
                return unlinkGreenlightJobToCandidateMutation.mutateAsync({});
            }
        },
        refreshGreenlightStatus: async () => {
            let updated = await refreshGreenlightStatusMutation.mutateAsync({});
            if (updated) {
                TB.toast("Greenlight Job status refreshed", {type: 'success'})
            }
            return updated;
        },
        updateTaxPreference: (params) => {
            return updateTaxPreferenceMutation.mutateAsync(params)
        },
        terminateCandidateForGreenLight: async () => {
            if (!position.assigned_candidate?.greenlight_id) {
                return;
            }
            let confirm = await TB.alert('warning', 'Terminate/Change',
                'Note: If you continue, the worker will be terminated in GreenLight. ' +
                'You can then submit another request for a different preferred tax class. ' +
                'If you do not want to terminate the greenlight job or ' +
                'you need to change which greenlight job this candidate should be linked to, ' +
                'select cancel and choose "Unlink Job" instead. Are you sure you want to continue? ', [
                    {title: 'Yes, Continue'},
                    {title: 'Cancel'}
                ]
            )

            if (confirm) {
                return terminateCandidateForGreenLightMutation.mutateAsync({})
            }
        },
    }

    if (isLoading) {
        return <LoadingBanner message="Loading" adjustOverlay/>;
    }

    return (
        <PositionOverrideContext.Provider value={value}>
            {children}
        </PositionOverrideContext.Provider>
    );
}

const usePositionOverride = () => {
    return React.useContext(PositionOverrideContext);
}
export default PositionOverrideProvider;
export {
    usePositionOverride,
    PositionOverrideContext
}